import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from './supabase';
import { logger } from './logger';

const TutorialContext = createContext();

// Tutorial step configurations by role
export const TUTORIAL_CONFIGS = {
  branch_admin: [
    {
      id: 'welcome',
      target: null, // No target = centered modal
      title: 'Witaj w panelu administratora!',
      description: 'Pokażemy Ci najważniejsze funkcje platformy. Możesz pominąć samouczek w dowolnym momencie.',
      position: 'center'
    },
    {
      id: 'nav-children',
      target: '[data-tutorial="nav-children"]',
      title: 'Dzieci',
      description: 'Tu zarządzasz listą dzieci w placówce. Możesz dodawać nowe dzieci i przypisywać je do grup.',
      position: 'right'
    },
    {
      id: 'nav-groups',
      target: '[data-tutorial="nav-groups"]',
      title: 'Grupy',
      description: 'Twórz i zarządzaj grupami. Przypisuj dzieci i nauczycieli do odpowiednich grup.',
      position: 'right'
    },
    {
      id: 'nav-teachers',
      target: '[data-tutorial="nav-teachers"]',
      title: 'Nauczyciele',
      description: 'Dodawaj nauczycieli i zarządzaj ich uprawnieniami. Każdy nauczyciel otrzyma zaproszenie email.',
      position: 'right'
    },
    {
      id: 'nav-parents',
      target: '[data-tutorial="nav-parents"]',
      title: 'Rodzice',
      description: 'Przeglądaj listę rodziców i ich powiązania z dziećmi.',
      position: 'right'
    },
    {
      id: 'nav-announcements',
      target: '[data-tutorial="nav-announcements"]',
      title: 'Ogłoszenia',
      description: 'Publikuj ogłoszenia dla rodziców. Możesz kierować je do wszystkich lub wybranych grup.',
      position: 'right'
    },
    {
      id: 'nav-settings',
      target: '[data-tutorial="nav-settings"]',
      title: 'Ustawienia',
      description: 'Konfiguruj ustawienia placówki: strukturę jadłospisu, kryteria ocen, odznaki i wiele więcej.',
      position: 'right'
    },
    {
      id: 'nav-billing',
      target: '[data-tutorial="nav-billing"]',
      title: 'Rozliczenia',
      description: 'Zarządzaj subskrypcją i płatnościami. Tu dodasz metodę płatności po okresie próbnym.',
      position: 'right'
    },
    {
      id: 'complete',
      target: null,
      title: 'Gotowe!',
      description: 'Znasz już podstawy. Pamiętaj, że możesz wrócić do pomocy w każdej chwili z menu ustawień.',
      position: 'center'
    }
  ],
  teacher: [
    {
      id: 'welcome',
      target: null,
      title: 'Witaj w panelu nauczyciela!',
      description: 'Pokażemy Ci najważniejsze funkcje platformy.',
      position: 'center'
    },
    {
      id: 'nav-home',
      target: '[data-tutorial="nav-home"]',
      title: 'Strona główna',
      description: 'Tu widzisz podsumowanie dnia: obecność dzieci, ostatnie aktualizacje i ogłoszenia.',
      position: 'right'
    },
    {
      id: 'nav-daily',
      target: '[data-tutorial="nav-daily"]',
      title: 'Aktualizacje dzienne',
      description: 'Dodawaj codzienne oceny dla dzieci. Rodzice zobaczą je w aplikacji.',
      position: 'right'
    },
    {
      id: 'nav-photos',
      target: '[data-tutorial="nav-photos"]',
      title: 'Zdjęcia',
      description: 'Dodawaj zdjęcia z zajęć. Rodzice mogą je przeglądać i pobierać.',
      position: 'right'
    },
    {
      id: 'nav-menu',
      target: '[data-tutorial="nav-menu"]',
      title: 'Jadłospis',
      description: 'Przeglądaj tygodniowy jadłospis. W zależności od ustawień możesz też go edytować.',
      position: 'right'
    },
    {
      id: 'nav-messages',
      target: '[data-tutorial="nav-messages"]',
      title: 'Wiadomości',
      description: 'Komunikuj się z rodzicami poprzez wiadomości.',
      position: 'right'
    },
    {
      id: 'complete',
      target: null,
      title: 'Gotowe!',
      description: 'Znasz już podstawy. Miłej pracy!',
      position: 'center'
    }
  ],
  parent: [
    {
      id: 'welcome',
      target: null,
      title: 'Witaj w KConnect!',
      description: 'Pokażemy Ci jak korzystać z aplikacji, aby być na bieżąco z życiem Twojego dziecka w placówce.',
      position: 'center'
    },
    {
      id: 'nav-home',
      target: '[data-tutorial="nav-home"]',
      title: 'Strona główna',
      description: 'Tu widzisz dzisiejsze informacje o dziecku: oceny, odznaki i ogłoszenia.',
      position: 'top'
    },
    {
      id: 'nav-children',
      target: '[data-tutorial="nav-children"]',
      title: 'Moje dzieci',
      description: 'Przeglądaj historię ocen i aktywności Twoich dzieci.',
      position: 'top'
    },
    {
      id: 'nav-photos',
      target: '[data-tutorial="nav-photos"]',
      title: 'Zdjęcia',
      description: 'Przeglądaj i pobieraj zdjęcia z zajęć.',
      position: 'top'
    },
    {
      id: 'nav-menu',
      target: '[data-tutorial="nav-menu"]',
      title: 'Jadłospis',
      description: 'Sprawdzaj tygodniowy jadłospis placówki.',
      position: 'top'
    },
    {
      id: 'nav-messages',
      target: '[data-tutorial="nav-messages"]',
      title: 'Wiadomości',
      description: 'Komunikuj się z nauczycielami Twojego dziecka.',
      position: 'top'
    },
    {
      id: 'complete',
      target: null,
      title: 'Gotowe!',
      description: 'Znasz już podstawy. Życzymy miłego korzystania z aplikacji!',
      position: 'center'
    }
  ]
};

export const TutorialProvider = ({ children }) => {
  const { userProfile, updateProfile } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tutorialConfig, setTutorialConfig] = useState([]);

  // Get user's role for tutorial config
  const userRole = userProfile?.active_role || userProfile?.primary_role || 'parent';

  // Initialize tutorial config based on role
  useEffect(() => {
    const config = TUTORIAL_CONFIGS[userRole] || TUTORIAL_CONFIGS.parent;
    setTutorialConfig(config);
  }, [userRole]);

  // Start the tutorial
  const startTutorial = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  // End the tutorial
  const endTutorial = useCallback(async (completed = false) => {
    setIsActive(false);
    setCurrentStep(0);

    // Save completion status
    if (completed && userProfile) {
      try {
        await updateProfile({
          metadata: {
            ...userProfile.metadata,
            tutorial_completed: true,
            tutorial_completed_at: new Date().toISOString()
          }
        });
      } catch (error) {
        logger.error('Error saving tutorial completion:', error);
      }
    }
  }, [userProfile, updateProfile]);

  // Go to next step
  const nextStep = useCallback(() => {
    if (currentStep < tutorialConfig.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      endTutorial(true);
    }
  }, [currentStep, tutorialConfig.length, endTutorial]);

  // Go to previous step
  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  // Skip tutorial
  const skipTutorial = useCallback(() => {
    endTutorial(false);
  }, [endTutorial]);

  // Get current step data
  const currentStepData = tutorialConfig[currentStep] || null;

  // Check if tutorial should auto-start (new user, hasn't completed tutorial)
  const shouldAutoStart = userProfile?.metadata?.onboarding_completed &&
                          !userProfile?.metadata?.tutorial_completed;

  const value = {
    isActive,
    currentStep,
    currentStepData,
    totalSteps: tutorialConfig.length,
    startTutorial,
    endTutorial,
    nextStep,
    prevStep,
    skipTutorial,
    shouldAutoStart
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);

  // Return a safe default state if context isn't available yet
  // This can happen during auth state transitions or initial render
  if (!context) {
    return {
      isActive: false,
      currentStep: 0,
      currentStepData: null,
      totalSteps: 0,
      startTutorial: () => {},
      endTutorial: () => {},
      nextStep: () => {},
      prevStep: () => {},
      skipTutorial: () => {},
      shouldAutoStart: false
    };
  }

  return context;
};
